import { useTranslation } from "react-i18next";

import { formatCount, formatMoney, toAmount, usePagedRows } from "../../../../shared/components/reports";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import type { PractitionerReport, ReportLinks, ReportMentorRow } from "../../domain/PractitionerReport";
import MaybeLink from "./MaybeLink";

const HEADING = "px-2 py-2 text-xs font-medium text-content-tertiary";
const GROUP = "border-b border-gray-200 px-2 pt-2 pb-1 text-center text-xs font-semibold text-content-secondary dark:border-gray-800";
const CELL = "px-2 py-2.5 text-content-primary";
const NUMBER = `${CELL} text-right tabular-nums`;

type Column = { key: string; short: string; label: string; value: (row: ReportMentorRow) => number };

/**
 * Each professional's share of a report across several — the centre's, a
 * course's, a student's across mentors: how many sessions of each kind, how
 * they ended, how many were moved, how many are billable, and what they add
 * up to at each one's own rates.
 */
export default function MentorTable({
    report,
    locale,
    links,
    only,
}: {
    report: PractitionerReport;
    locale: string;
    links: ReportLinks;
    /** One professional's row alone, when the page is filtered to them. */
    only?: string;
}) {
    const { t } = useTranslation();
    const rows = only ? report.mentors.filter((row) => row.therapist_id === only) : report.mentors;
    const paged = usePagedRows(rows);
    const count = (value: number) => formatCount(value, locale);
    const byType: Column[] = [
        { key: "individual", short: t("reports.mentors.short.individual"), label: t("reports.type.individual"), value: (r) => r.individual },
        { key: "couple", short: t("reports.mentors.short.couple"), label: t("reports.type.couple"), value: (r) => r.couple },
        { key: "group", short: t("reports.mentors.short.group"), label: t("reports.type.group"), value: (r) => r.group },
    ];
    const byStatus: Column[] = [
        { key: "finished", short: t("reports.mentors.short.finished"), label: t("reports.status.finished"), value: (r) => r.finished },
        { key: "pending", short: t("reports.mentors.short.pending"), label: t("reports.status.pending"), value: (r) => r.pending },
        { key: "cancelled", short: t("reports.mentors.short.cancelled"), label: t("reports.status.cancelled"), value: (r) => r.cancelled },
        { key: "rescheduled", short: t("reports.mentors.short.rescheduled"), label: t("reports.mentors.rescheduled"), value: (r) => r.rescheduled },
    ];
    const abbr = (column: Column) =>
        column.short === column.label ? column.label : <abbr title={column.label} className="no-underline">{column.short}</abbr>;

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h2 className="mb-4 text-base font-semibold text-content-primary">{t("reports.mentors.title")}</h2>
            {rows.length === 0 ? (
                <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                    {t("reports.empty")}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    {/* Two header rows: the counts by kind and by outcome are grouped
                        under one heading each, so thirteen figures fit the page. */}
                    <table className="w-full min-w-[720px] text-left text-sm">
                        <thead>
                            <tr>
                                <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 pl-0 align-bottom dark:border-gray-800`}>
                                    {t("reports.mentors.name")}
                                </th>
                                <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 text-right align-bottom dark:border-gray-800`}>
                                    {t("reports.table.sessions")}
                                </th>
                                <th scope="colgroup" colSpan={byType.length} className={GROUP}>
                                    {t("reports.mentors.byType")}
                                </th>
                                <th scope="colgroup" colSpan={byStatus.length} className={GROUP}>
                                    {t("reports.mentors.byStatus")}
                                </th>
                                <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 text-right align-bottom dark:border-gray-800`}>
                                    {t("reports.mentors.billable")}
                                </th>
                                <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 text-right align-bottom dark:border-gray-800`}>
                                    {t("reports.mentors.people")}
                                </th>
                                <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 text-right align-bottom dark:border-gray-800`}>
                                    {t("reports.mentors.hours")}
                                </th>
                                {report.has_amounts && (
                                    <th scope="col" rowSpan={2} className={`${HEADING} border-b border-gray-200 pr-0 text-right align-bottom dark:border-gray-800`}>
                                        {t("reports.table.amount")}
                                    </th>
                                )}
                            </tr>
                            <tr className="border-b border-gray-200 dark:border-gray-800">
                                {[...byType, ...byStatus].map((column) => (
                                    <th key={column.key} scope="col" className={`${HEADING} text-right`}>
                                        {abbr(column)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.page.map((row) => (
                                <tr key={row.therapist_id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                    <th scope="row" className={`${CELL} pl-0 font-medium`}>
                                        <MaybeLink to={links.mentor(row.therapist_id)}>{row.name}</MaybeLink>
                                    </th>
                                    <td className={NUMBER}>{count(row.sessions)}</td>
                                    {[...byType, ...byStatus].map((column) => (
                                        <td key={column.key} className={NUMBER}>
                                            {count(column.value(row))}
                                        </td>
                                    ))}
                                    <td className={`${NUMBER} font-semibold`}>{count(row.billable)}</td>
                                    <td className={NUMBER}>{count(row.subjects)}</td>
                                    <td className={NUMBER}>
                                        {(row.minutes / 60).toLocaleString(locale, { maximumFractionDigits: 1 })}
                                    </td>
                                    {report.has_amounts && (
                                        <td className={`${NUMBER} whitespace-nowrap pr-0`}>
                                            {row.amount === null
                                                ? t("reports.table.noRate")
                                                : formatMoney(toAmount(row.amount), locale)}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Pagination
                offset={paged.offset}
                total={paged.total}
                pageSize={paged.pageSize}
                onPrev={paged.onPrev}
                onNext={paged.onNext}
            />
        </section>
    );
}
